import { addDays, businessDayKey } from "@milqit/domain";
import {
  type Customer,
  type CustomerListQuery,
  type Delivery,
  type DeliveryAgent,
  type DeliveryStatus,
  type Order,
  type OrderQuery,
  type OrderStatus,
  type Paginated,
} from "@milqit/shared-types";
import type {
  AdminCustomerValues,
  DeliveryAssignmentValues,
  OrderStatusUpdateValues,
} from "@milqit/validations";

import { apiFetch, apiFetchOrNull, qs } from "./client";

/* Orders ------------------------------------------------------------------ */

export async function listOrders(
  query: OrderQuery & { page: number; pageSize: number },
): Promise<Paginated<Order> & { counts: Partial<Record<OrderStatus | "all", number>> }> {
  return apiFetch(`/admin/orders${qs({ ...query })}`);
}

export async function getOrder(id: string): Promise<Order | null> {
  return apiFetchOrNull(`/admin/orders/${id}`);
}

export async function updateOrderStatus(
  id: string,
  values: OrderStatusUpdateValues,
): Promise<Order> {
  return apiFetch(`/admin/orders/${id}/status`, { method: "POST", body: JSON.stringify(values) });
}

/* Customers --------------------------------------------------------------- */

export async function listCustomers(query: CustomerListQuery): Promise<Paginated<Customer>> {
  return apiFetch(`/admin/customers${qs({ ...query })}`);
}

export async function getCustomer(
  id: string,
): Promise<{ customer: Customer; orders: Order[] } | null> {
  return apiFetchOrNull(`/admin/customers/${id}`);
}

export async function updateCustomer(id: string, values: AdminCustomerValues): Promise<Customer> {
  return apiFetch(`/admin/customers/${id}`, { method: "PUT", body: JSON.stringify(values) });
}

/* Deliveries -------------------------------------------------------------- */

export interface DeliveryQuery {
  day: "today" | "tomorrow" | "all";
  status?: DeliveryStatus | "all";
  agentId?: string;
  window?: string;
  q?: string;
  page: number;
  pageSize: number;
}

export async function listDeliveries(
  query: DeliveryQuery,
): Promise<Paginated<Delivery> & { counts: Record<DeliveryStatus, number>; unassigned: number }> {
  return apiFetch(`/admin/deliveries${qs({ ...query })}`);
}

export async function listAgents(): Promise<(DeliveryAgent & { assigned: number })[]> {
  return apiFetch(`/admin/delivery-agents`);
}

export async function updateDelivery(
  id: string,
  values: DeliveryAssignmentValues,
): Promise<Delivery> {
  return apiFetch(`/admin/deliveries/${id}`, { method: "PUT", body: JSON.stringify(values) });
}

/* Dashboard --------------------------------------------------------------- */

export interface DashboardSummary {
  revenueByDay: { date: string; revenue: number; orders: number }[];
  revenue7d: number;
  revenuePrev7d: number;
  ordersTomorrow: number;
  ordersToday: number;
  /** Drops marked delivered this morning, as the Deliveries page counts them. */
  deliveredToday: number;
  activePlans: number;
  customers: number;
  /** Every item low or out of stock (`stockStatus`); `lowStock` lists the worst few. */
  lowStockCount: number;
  lowStock: {
    productId: string;
    name: string;
    image: string;
    available: number;
    reorderLevel: number;
  }[];
  statusBreakdown: { status: OrderStatus; count: number }[];
  recentOrders: Order[];
  deliveriesTomorrow: Record<DeliveryStatus, number> & { unassigned: number; total: number };
}

export async function getDashboardSummary(): Promise<DashboardSummary> {
  return apiFetch(`/admin/dashboard`);
}

/**
 * The mornings screens work with: the real IST business days. Getters, so a dashboard left open past
 * midnight moves on to the new day.
 */
export const BUSINESS_DAYS = {
  get today() {
    return businessDayKey(new Date());
  },
  get tomorrow() {
    return addDays(businessDayKey(new Date()), 1);
  },
};
